import clarity from '@microsoft/clarity';

// Clarity configuration
const CLARITY_PROJECT_ID = 'sg1wyhxehr';

// Extend Window interface to include clarity
declare global {
  interface Window {
    clarity?: {
      setTag: (eventName: string, data: string) => void;
    };
  }
}

// UTM Parameter tracking
interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
}

// Extract UTM parameters from URL
const getUTMParams = (): UTMParams => {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_term: urlParams.get('utm_term') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,
    gclid: urlParams.get('gclid') || undefined,
  };
};

// Store UTM parameters for session
let sessionUTMParams: UTMParams = {};

// Check if Clarity is available
const isClarityAvailable = () => {
  return typeof window !== 'undefined' && window.clarity && typeof window.clarity.setTag === 'function';
};

// Initialize Clarity
export const initializeClarity = () => {
  if (process.env.NODE_ENV === 'production') {
    clarity.init(CLARITY_PROJECT_ID);
    
    // Capture UTM parameters on initialization
    sessionUTMParams = getUTMParams();
    
    // Track UTM parameters if present
    if (Object.keys(sessionUTMParams).some(key => sessionUTMParams[key as keyof UTMParams])) {
      trackClarityEvent('utm_parameters', sessionUTMParams);
    }
  }
};

// Track custom events with UTM context
export const trackClarityEvent = (eventName: string, data?: Record<string, any>) => {
  if (isClarityAvailable()) {
    const eventData = {
      ...data,
      ...sessionUTMParams,
    };
    clarity.setTag(eventName, JSON.stringify(eventData));
  }
};

// Track signup success
export const trackSignupSuccess = (email: string, formLocation: string, source: string = 'landing-page') => {
  trackClarityEvent('signup_success', {
    email_domain: email.split('@')[1],
    form_location: formLocation,
    source: source,
    timestamp: new Date().toISOString(),
  });
};

// Track signup error
export const trackSignupError = (error: string, formLocation: string) => {
  trackClarityEvent('signup_error', {
    error_message: error,
    form_location: formLocation,
    timestamp: new Date().toISOString(),
  });
};

// Track page views
export const trackPageView = (pageName: string) => {
  trackClarityEvent('page_view', {
    page_name: pageName,
    timestamp: new Date().toISOString(),
  });
};

// Track CTA clicks
export const trackCTAClick = (ctaType: string, location: string) => {
  trackClarityEvent('cta_click', {
    cta_type: ctaType,
    location: location,
    timestamp: new Date().toISOString(),
  });
};

// Track form interactions
export const trackFormInteraction = (action: string, formLocation: string) => {
  trackClarityEvent('form_interaction', {
    action: action,
    form_location: formLocation,
    timestamp: new Date().toISOString(),
  });
};

// Get current UTM parameters for external use
export const getCurrentUTMParams = (): UTMParams => {
  return { ...sessionUTMParams };
};

// Manually set UTM parameters (for SPA navigation)
export const setUTMParams = (params: UTMParams) => {
  sessionUTMParams = { ...sessionUTMParams, ...params };
}; 